<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">進貨管理</h1>
      </div>
      <div class="header-actions">
        <router-link to="/client/inventory/purchase-orders/create" class="btn btn-primary">
                <i class="ri-add-line"></i> 新增進貨單
              </router-link>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <select v-model="filters.status" @change="fetchOrders" class="form-control">
          <option value="">全部狀態</option>
          <option value="draft">草稿</option>
          <option value="confirmed">已確認</option>
          <option value="partially_received">部分入庫</option>
          <option value="completed">已完成</option>
          <option value="cancelled">已取消</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="filters.warehouseId" @change="fetchOrders" class="form-control">
          <option value="">全部倉庫</option>
          <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.warehouseName }}</option>
        </select>
      </div>
    </div>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>單號</th>
            <th>入庫倉庫</th>
            <th>供應商</th>
            <th>預計到貨</th>
            <th>採購金額</th>
            <th>狀態</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading"><td colspan="8" class="text-center">載入中...</td></tr>
          <tr v-else-if="!orders.length"><td colspan="8" class="text-center text-muted">尚無進貨單</td></tr>
          <tr v-for="o in orders" :key="o.id">
            <td><code>{{ o.orderNo }}</code></td>
            <td>{{ o.warehouseName }}</td>
            <td>{{ o.supplierName || '-' }}</td>
            <td>{{ o.expectedDate ? o.expectedDate.slice(0, 10) : '-' }}</td>
            <td>{{ formatCurrency(o.totalAmount) }}</td>
            <td><span :class="['badge', statusClass(o.status)]">{{ statusLabel(o.status) }}</span></td>
            <td>{{ formatDate(o.createdAt) }}</td>
            <td>
              <router-link :to="`/client/inventory/purchase-orders/${o.id}`" class="btn btn-sm btn-outline">
                查看
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="pagination"
      :page="pagination.currentPage"
      :total-pages="pagination.totalPages"
      :total="pagination.totalItems"
      @change="changePage"
    />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { purchaseOrderService } from '@/api/modules/warehouses.js';
import { warehouseService } from '@/api/modules/warehouses.js';

const orders = ref([]);
const warehouses = ref([]);
const loading = ref(false);
const pagination = ref(null);
const filters = ref({ status: '', warehouseId: '', page: 1, pageSize: 20 });

const STATUS_LABELS = { draft: '草稿', confirmed: '已確認', partially_received: '部分入庫', completed: '已完成', cancelled: '已取消' };
const STATUS_CLASSES = { draft: 'badge-secondary', confirmed: 'badge-primary', partially_received: 'badge-warning', completed: 'badge-success', cancelled: 'badge-danger' };

const statusLabel = (s) => STATUS_LABELS[s] || s;
const statusClass = (s) => STATUS_CLASSES[s] || 'badge-secondary';
const formatCurrency = (v) => `$${Number(v || 0).toLocaleString()}`;
const formatDate = (d) => d ? new Date(d).toLocaleDateString('zh-TW') : '-';

async function fetchOrders() {
  loading.value = true;
  try {
    const params = { page: filters.value.page, pageSize: filters.value.pageSize };
    if (filters.value.status) params.status = filters.value.status;
    if (filters.value.warehouseId) params.warehouseId = filters.value.warehouseId;
    const res = await purchaseOrderService.getPurchaseOrders(params);
    orders.value = res.data?.items || [];
    pagination.value = res.data?.pagination || null;
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
}

async function fetchWarehouses() {
  try {
    const res = await warehouseService.getWarehouses({ status: 'active' });
    warehouses.value = res.data?.items || [];
  } catch (e) { console.error(e); }
}

function changePage(page) {
  filters.value.page = page;
  fetchOrders();
}

onMounted(() => { fetchWarehouses(); fetchOrders(); });
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';
.table-wrapper table { min-width: 900px; }
.badge { padding: 2px 8px; border-radius: 20px; font-size: 12px; font-weight: 500; }
.badge-primary { background: #e3f0ff; color: #1a73e8; }
.badge-secondary { background: #f0f0f0; color: #666; }
.badge-success { background: #e6f9ee; color: #27ae60; }
.badge-danger { background: #fde8e8; color: #e74c3c; }
.badge-warning { background: #fff8e1; color: #f39c12; }
.btn { padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 14px; border: none; text-decoration: none; display: inline-block; }
.btn-primary { background: #1a73e8; color: #fff; }
.btn-outline { background: transparent; border: 1px solid #1a73e8; color: #1a73e8; }
.btn-sm { padding: 4px 10px; font-size: 12px; border-radius: 4px; }
.btn:disabled { opacity: .6; cursor: not-allowed; }
.pagination-wrapper { display: flex; align-items: center; gap: 10px; margin-top: 16px; justify-content: flex-end; }
.pagination-info { color: #666; font-size: 13px; }
.pagination-page { color: #333; font-size: 13px; }
</style>
